/**
 * Selección múltiple de filas, al estilo de una bandeja de correo.
 *
 * Vive aquí y no dentro de cada sección porque las cuatro listas del taller
 * —Páginas, Contenido, Formularios y Contactos— necesitan lo mismo: una casilla
 * por fila, otra que manda sobre todas, y una barra que cambia de contenido
 * según haya algo marcado o no. Escrito cuatro veces, a la cuarta ya no serían
 * iguales.
 *
 * La selección es SIEMPRE de la página visible. No se guardan ids de páginas
 * anteriores: «seleccionar todo» tendría que significar «los 25 del total» o
 * «los 10 que ves», y ofrecer lo primero sin poder enseñarlo lleva a borrar
 * cosas que nunca se llegaron a mirar.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { IconMinus, IconCheck, IconDots, IconRefresh, IconStar, IconStarFilled } from '@tabler/icons-react'
import { useI18n } from '../../lib/i18n'

export type ListSelection = {
  selected: Set<string>
  count: number
  isSelected: (id: string) => boolean
  toggle: (id: string) => void
  /** Marca o desmarca de golpe todo lo que hay a la vista. */
  toggleAll: () => void
  clear: () => void
  allVisible: boolean
  someVisible: boolean
  /** Los ids marcados, en el orden en que aparecen en la lista. */
  ids: string[]
}

export function useListSelection(visibleIds: string[]): ListSelection {
  const [selected, setSelected] = useState<Set<string>>(() => new Set())

  // Al cambiar de página, de filtro o al borrarse una fila, lo que ya no está a
  // la vista deja de contar. Sin esto, la barra podría decir «3 seleccionados»
  // señalando filas que nadie tiene delante.
  const visibleKey = visibleIds.join('|')
  useEffect(() => {
    setSelected((current) => {
      if (current.size === 0) return current
      const visible = new Set(visibleIds)
      const next = new Set([...current].filter((id) => visible.has(id)))
      return next.size === current.size ? current : next
    })
    // `visibleKey` y no el array: llega nuevo en cada render y reejecutaría esto
    // siempre, vaciando la selección al primer repintado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey])

  const ids = useMemo(() => visibleIds.filter((id) => selected.has(id)), [visibleIds, selected])
  const allVisible = visibleIds.length > 0 && ids.length === visibleIds.length

  return {
    selected,
    count: ids.length,
    ids,
    allVisible,
    someVisible: ids.length > 0 && !allVisible,
    isSelected: (id) => selected.has(id),
    toggle: (id) => setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    }),
    toggleAll: () => setSelected((current) => {
      const visible = new Set(visibleIds)
      const allOn = visibleIds.length > 0 && visibleIds.every((id) => current.has(id))
      if (allOn) return new Set([...current].filter((id) => !visible.has(id)))
      return new Set([...current, ...visibleIds])
    }),
    clear: () => setSelected(new Set()),
  }
}

/**
 * La casilla. Es un `button` y no un `input[type=checkbox]` a propósito: el
 * estado intermedio —algunas filas marcadas— no se puede pintar en un checkbox
 * nativo sin tocarlo por JS, y aquí se necesita en la cabecera. `aria-checked`
 * con `mixed` dice lo mismo a quien no lo ve.
 */
export function SelectCheckbox({ checked, mixed = false, label, disabled = false, title, onChange }: {
  checked: boolean
  mixed?: boolean
  label: string
  disabled?: boolean
  title?: string | undefined
  onChange: () => void
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={mixed ? 'mixed' : checked}
      aria-label={label}
      disabled={disabled}
      title={title}
      className={`dash-row-check${checked || mixed ? ' is-on' : ''}`}
      onClick={(event) => { event.stopPropagation(); onChange() }}
    >
      {mixed ? <IconMinus size={12} stroke={3} /> : checked ? <IconCheck size={12} stroke={3} /> : null}
    </button>
  )
}

/** La estrella de una fila. Ver [[dashboardStars]] sobre dónde se guarda. */
export function StarButton({ starred, label, onToggle }: {
  starred: boolean
  label: string
  onToggle: () => void
}) {
  const { t } = useI18n()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null)
  const tooltip = starred ? t('common.unstar') : t('common.star')

  const showTooltip = () => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return
    const edge = 12
    const estimatedWidth = Math.min(180, tooltip.length * 7 + 20)
    const center = rect.left + rect.width / 2
    const left = Math.max(edge + estimatedWidth / 2, Math.min(window.innerWidth - edge - estimatedWidth / 2, center))
    setTooltipPosition({ top: rect.top - 8, left })
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-pressed={starred}
        aria-label={label}
        className={`dash-row-star${starred ? ' is-on' : ''}`}
        onPointerEnter={showTooltip}
        onPointerLeave={() => setTooltipPosition(null)}
        onFocus={showTooltip}
        onBlur={() => setTooltipPosition(null)}
        onClick={(event) => { event.stopPropagation(); onToggle() }}
      >
        {starred ? <IconStarFilled size={15} /> : <IconStar size={15} />}
      </button>
      {tooltipPosition ? createPortal(
        <span
          className="dash-portal-tooltip"
          role="tooltip"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          {tooltip}
        </span>,
        document.body,
      ) : null}
    </>
  )
}

/**
 * La barra de encima de la lista.
 *
 * No hay botón de «quitar selección»: para eso está la casilla de la cabecera,
 * que con algo marcado lo desmarca todo. Dos mandos para lo mismo, uno al lado
 * del otro, sólo obligan a decidir cuál usar.
 *
 * Sin nada marcado enseña el recuento y la paginación, pegados a la derecha, y
 * un botón de recargar donde luego irán las acciones — ese hueco estaba vacío y
 * es el sitio donde uno ya está mirando cuando quiere ver si ha entrado algo
 * nuevo. Con algo marcado, la izquierda pasa a ser la barra de acciones; el
 * recuento sigue a la derecha, porque perder de vista en qué página estás
 * mientras decides qué borrar es justo cuando peor viene.
 */
export function ListToolbar({ selection, actions = [], children }: {
  selection: ListSelection
  /**
   * Acciones en bloque; sólo se ven con algo marcado. Se describen en vez de
   * pasarse ya dibujadas porque la barra las pinta de dos formas —una fila de
   * iconos en pantalla ancha, un menú en el teléfono— y con los botones ya
   * hechos no habría manera de sacar de ellos el nombre para las filas del
   * menú.
   */
  actions?: BulkActionSpec[]
  /** Vuelve a pedir la lista. Sin esto no se enseña el botón. */
  /** La paginación, o lo que vaya a la derecha. */
  children?: ReactNode
}) {
  const { t } = useI18n()
  if (selection.count === 0 && !children) return null

  return (
    <div className={`dash-list-toolbar${selection.count > 0 ? ' has-selection' : ''}`}>
      <div className="dash-list-toolbar-actions">
        {selection.count > 0 ? (
          <>
            <span className="dash-list-toolbar-count">
              {selection.count === 1
                ? t('common.selection.one', { count: selection.count })
                : t('common.selection.many', { count: selection.count })}
            </span>
            {/* Las dos formas van siempre en el árbol y es el CSS quien elige:
                cuál toca depende del ancho de la ventana, que cambia sin que
                haya nada que volver a dibujar. */}
            <span className="dash-list-toolbar-bulk">
              {actions.map((action) => <BulkAction key={action.label} {...action} />)}
            </span>
            {actions.length > 0 ? <BulkActionsMenu actions={actions} /> : null}
          </>
        ) : null}
      </div>
      <div className="dash-list-toolbar-info">{children}</div>
    </div>
  )
}

/**
 * Un botón de la barra de acciones en bloque: sólo icono.
 *
 * El texto pasa al globo negro que ya usan los demás botones de icono del
 * taller. Con seis acciones escritas, la barra ocupaba toda la fila y el
 * recuento de la derecha quedaba arrinconado; y son acciones que se usan a
 * diario, así que el dibujo se aprende enseguida. El nombre sigue estando para
 * quien no ve el globo, en `aria-label`.
 */
export function BulkAction({ icon, label, danger, onClick }: BulkActionSpec) {
  return (
    <button
      type="button"
      className={`dash-bulk-action${danger ? ' is-danger' : ''}`}
      aria-label={label}
      data-tooltip={label}
      onClick={onClick}
    >
      {icon}
    </button>
  )
}

/** Una acción en bloque, sin decidir todavía cómo se dibuja. */
export type BulkActionSpec = {
  icon: ReactNode
  label: string
  danger?: boolean
  onClick: () => void
}

/**
 * Las mismas acciones, recogidas en un botón de tres puntos.
 *
 * Es lo que se ve en el teléfono. Ahí la fila de iconos se partía en dos
 * renglones por debajo del recuento —cinco cuadros de 32 no caben en 360 de
 * ancho— y dejaba la barra ocupando más alto que las propias filas que uno
 * acaba de marcar. Un solo botón, y las acciones con su nombre escrito dentro
 * del desplegable negro del taller: en el teléfono no hay globo que enseñe qué
 * hace cada dibujo, así que ahí el nombre importa más que el ahorro de sitio.
 */
function BulkActionsMenu({ actions }: { actions: BulkActionSpec[] }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className={`dash-bulk-menu-wrap${open ? ' is-open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className="dash-bulk-action dash-bulk-menu-btn"
        aria-label={t('common.selection.actions')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <IconDots size={16} />
      </button>

      {open ? (
        <div className="project-menu dash-bulk-menu" role="menu">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              className={`project-menu-item${action.danger ? ' danger' : ''}`}
              onClick={() => { setOpen(false); action.onClick() }}
            >
              {action.icon} {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * «Actualizar», junto a la búsqueda.
 *
 * Vivía en la barra de abajo, la de las acciones en bloque, y sólo aparecía
 * cuando no había nada seleccionado: se escondía justo cuando alguien estaba
 * trabajando con la lista. Y era el único mando de esa barra que no tiene que
 * ver con la selección.
 *
 * Aquí está siempre y junto a los demás mandos de la lista —buscar, filtrar,
 * ordenar—, que es a lo que pertenece.
 */
export function RefreshButton({ refreshing, onRefresh }: {
  refreshing: boolean
  onRefresh: () => void
}) {
  const { t } = useI18n()
  return (
    <button
      type="button"
      className="dash-filter-trigger dash-toolbar-refresh"
      aria-label={t('common.refreshList')}
      data-tooltip={t('common.refresh')}
      disabled={refreshing}
      onClick={onRefresh}
    >
      <IconRefresh className={refreshing ? 'dash-spin' : ''} size={15} aria-hidden="true" />
    </button>
  )
}

/**
 * «Actualizar» en la cabecera de la sección, cuando la lista no tiene barra de
 * búsqueda donde ponerlo.
 *
 * Suelto al lado del cupo del plan —que va en su caja— se leía como un icono
 * perdido, así que va en una caja igual: las mismas clases que el cupo
 * (`PlanUsageSummary`), para que las dos piezas de la cabecera sean del mismo
 * material. En la barra de búsqueda se usa `RefreshButton` a secas: ahí ya
 * está dentro de una caja, la de la barra.
 */
export function HeaderRefreshButton({ refreshing, onRefresh }: {
  refreshing: boolean
  onRefresh: () => void
}) {
  const { t } = useI18n()
  return (
    <div className="dash-toolbar dash-layered-toolbar dash-plan-usage-toolbar dash-header-tool">
      <button
        type="button"
        className="dash-plan-usage-info"
        aria-label={t('common.refreshList')}
        data-tooltip={t('common.refresh')}
        disabled={refreshing}
        onClick={onRefresh}
      >
        <IconRefresh className={refreshing ? 'dash-spin' : ''} size={18} aria-hidden="true" />
      </button>
    </div>
  )
}
